import type { CSSProperties } from 'react';
import type { RowData } from '@tanstack/react-table';
import classNames from 'classnames';

import ColumnSelector from './ColumnSelector';
import DataTableBody from './DataTableBody';
import DataTableFooter from './DataTableFooter';
import DataTableHeader from './DataTableHeader';
import DataTableScroll from './DataTableScroll';
import type { DataTableInstance } from './features';
import PaginationControls from './PaginationControls';
import type { DataTableProps } from './types';
import { useDataTable } from './useDataTable';

import styles from './DataTable.module.scss';
import gridStyles from './GridLayout.module.scss';
import tableStyles from './TableLayout.module.scss';

export type { DataTableProps, DataTableRowProps, DataTableRowVariant } from './types';

const SKELETON_HEIGHT = 300;

/** The grid layout's custom properties: the column tracks, row height and clamping. */
function gridStyle<T extends RowData>(
    table: DataTableInstance<T>,
    rowHeight: number | undefined,
    clampCells: boolean
): CSSProperties {
    const tracks = table
        .getVisibleLeafColumns()
        .map((column) => column.columnDef.meta?.width ?? '1fr')
        .join(' ');
    const style: Record<string, string> = { '--grid-table-columns': tracks };
    if (rowHeight !== undefined) style['--grid-table-row-height'] = `${rowHeight}px`;
    if (clampCells) {
        style['--grid-table-cell-min-width'] = '0';
        style['--grid-table-cell-overflow'] = 'hidden';
        style['--grid-table-min-width'] = '100%';
    }
    return style;
}

/**
 * Grouped columns get one `<colgroup>` per top-level column, so a group header's
 * `scope="colgroup"` has a group to name.
 */
function ColumnGroups<T extends RowData>(props: { table: DataTableInstance<T> }) {
    const headerGroups = props.table.getHeaderGroups();
    if (headerGroups.length < 2) return null;

    return (
        <>
            {headerGroups[0].headers.map((header) => (
                <colgroup key={header.id} span={header.colSpan > 1 ? header.colSpan : undefined} />
            ))}
        </>
    );
}

/**
 * The portal's one table, over @tanstack/react-table: sorting, filtering, pagination,
 * column visibility, expansion, a footer row and an empty state, in a native table or on
 * CSS grid tracks.
 */
export function DataTable<T extends RowData>(props: DataTableProps<T>) {
    const {
        layout = 'table',
        variant = 'data',
        density = 'regular',
        hover = variant === 'data',
        responsive = false,
        className,
        tableClassName,
        headerClassName,
        enableSorting = true,
        getRowProps,
        rowHeight,
        clampCells = false,
        isLoading = false,
        skeletonHeight = SKELETON_HEIGHT,
        emptyState,
        footer,
        footerClassName,
        renderExpanded,
        enablePagination = false,
        pageSizeOptions = [15, 25, 50, 100],
        onPageSizeChange,
        enableColumnSelector = false,
    } = props;
    const table = useDataTable(props);
    const isGrid = layout === 'grid';

    const tableElement = (
        <table
            className={classNames(
                isGrid ? gridStyles.gridTable : tableStyles.table,
                tableClassName
            )}
            data-layout={layout}
            data-variant={variant}
            data-density={density}
            data-hover={hover}
            data-paginated={enablePagination}
            aria-label={props['aria-label']}>
            {!isGrid && <ColumnGroups table={table} />}
            {variant === 'data' && (
                <DataTableHeader
                    table={table}
                    sortable={enableSorting}
                    grid={isGrid}
                    className={headerClassName}
                />
            )}
            <DataTableBody
                table={table}
                getRowProps={getRowProps}
                cellWidths={!isGrid}
                renderExpanded={renderExpanded}
                emptyState={emptyState}
                isLoading={isLoading}
                skeletonHeight={skeletonHeight}
            />
            {!isLoading && <DataTableFooter table={table} />}
        </table>
    );

    const scrolled = isGrid ? (
        <DataTableScroll className={gridStyles.gridScroll} label={props['aria-label']}>
            <div className={gridStyles.grid} style={gridStyle(table, rowHeight, clampCells)}>
                {tableElement}
            </div>
        </DataTableScroll>
    ) : responsive ? (
        <DataTableScroll className={styles.scroll} label={props['aria-label']}>
            {tableElement}
        </DataTableScroll>
    ) : (
        tableElement
    );

    return (
        <div
            className={classNames(styles.dataTable, className)}
            data-variant={variant}
            data-layout={layout}>
            {enableColumnSelector && (
                <div className={styles.tableToolbar}>
                    <ColumnSelector table={table} />
                </div>
            )}
            {scrolled}
            {footer !== undefined && (
                <div className={classNames(styles.footer, footerClassName)}>{footer}</div>
            )}
            {enablePagination && table.getPageCount() > 0 && (
                <PaginationControls
                    table={table}
                    pageSizeOptions={pageSizeOptions}
                    onPageSizeChange={onPageSizeChange}
                />
            )}
        </div>
    );
}

export default DataTable;
